import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, reportsAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { toast } from 'sonner';
import { Download, Calendar, X, TrendingUp, Users, CheckCircle, Clock, AlertTriangle, Banknote, Target, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';

import { formatCurrency, ORDER_STATUSES } from '../../lib/utils';
const CHART_COLORS = ['#0F172A', '#FF4F00', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const MONTHS = [
  { value: '1', label: 'Январь' }, { value: '2', label: 'Февраль' },
  { value: '3', label: 'Март' }, { value: '4', label: 'Апрель' },
  { value: '5', label: 'Май' }, { value: '6', label: 'Июнь' },
  { value: '7', label: 'Июль' }, { value: '8', label: 'Август' },
  { value: '9', label: 'Сентябрь' }, { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' }, { value: '12', label: 'Декабрь' },
];

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const getDateParams = useCallback(() => {
    const params = {};
    if (filterDateFrom) {
      params.date_from = filterDateFrom;
    } else if (filterYear !== 'all') {
      const m = filterMonth !== 'all' ? filterMonth.padStart(2, '0') : '01';
      params.date_from = `${filterYear}-${m}-01`;
    }
    if (filterDateTo) {
      params.date_to = filterDateTo;
    } else if (filterYear !== 'all') {
      if (filterMonth !== 'all') {
        const y = parseInt(filterYear);
        const mo = parseInt(filterMonth);
        const lastDay = new Date(y, mo, 0).getDate();
        params.date_to = `${y}-${String(mo).padStart(2, '0')}-${lastDay}`;
      } else {
        params.date_to = `${filterYear}-12-31`;
      }
    }
    return params;
  }, [filterYear, filterMonth, filterDateFrom, filterDateTo]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dashboardAPI.getStats(getDateParams());
      setStats(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [getDateParams]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const hasActiveFilters = filterYear !== 'all' || filterMonth !== 'all' || filterDateFrom || filterDateTo;

  const resetFilters = () => { setFilterYear('all'); setFilterMonth('all'); setFilterDateFrom(''); setFilterDateTo(''); };

  const setQuickFilter = (offset) => {
    const d = new Date(); d.setDate(d.getDate() + offset);
    const iso = d.toISOString().slice(0, 10);
    if (filterDateFrom === iso && filterDateTo === iso) { setFilterDateFrom(''); setFilterDateTo(''); }
    else { setFilterDateFrom(iso); setFilterDateTo(iso); setFilterYear('all'); setFilterMonth('all'); }
  };

  const isQuickActive = (offset) => {
    const d = new Date(); d.setDate(d.getDate() + offset);
    return filterDateFrom === d.toISOString().slice(0, 10) && filterDateTo === d.toISOString().slice(0, 10);
  };

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const getFilterLabel = () => {
    if (filterDateFrom && filterDateTo) {
      if (filterDateFrom === filterDateTo) return new Date(filterDateFrom).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      const from = new Date(filterDateFrom).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
      const to = new Date(filterDateTo).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
      return `${from} — ${to}`;
    }
    if (filterYear !== 'all' && filterMonth !== 'all') return `${MONTHS[parseInt(filterMonth) - 1].label} ${filterYear}`;
    if (filterYear !== 'all') return `${filterYear} год`;
    return null;
  };

  const exportOrdersCSV = async () => {
    try {
      const res = await reportsAPI.exportOrdersCSV();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'orders.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Заказы экспортированы');
    } catch { toast.error('Ошибка экспорта'); }
  };

  const exportTasksCSV = async () => {
    try {
      const res = await reportsAPI.exportTasksCSV();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'tasks.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Задачи экспортированы');
    } catch { toast.error('Ошибка экспорта'); }
  };

  const exportCustomersCSV = async () => {
    try {
      const res = await reportsAPI.exportCustomersCSV();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'customers.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Клиенты экспортированы');
    } catch { toast.error('Ошибка экспорта'); }
  };

  const exportTasksICS = async () => {
    try {
      const res = await reportsAPI.exportTasksICS();
      const blob = new Blob([res.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'tasks.ics'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Календарь экспортирован');
    } catch { toast.error('Ошибка экспорта'); }
  };

  const monthlyRevenueData = (() => {
    const allMonths = new Set([
      ...Object.keys(stats?.monthly_revenue || {}),
      ...Object.keys(stats?.monthly_manual_income || {}),
      ...Object.keys(stats?.monthly_expense || {}),
    ]);
    if (allMonths.size === 0) return [];
    return [...allMonths]
      .sort()
      .slice(-12)
      .map(month => ({
        month: new Date(month + '-01').toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' }),
        revenue: (stats?.monthly_revenue?.[month] || 0) + (stats?.monthly_manual_income?.[month] || 0),
        expense: stats?.monthly_expense?.[month] || 0,
        profit: ((stats?.monthly_revenue?.[month] || 0) + (stats?.monthly_manual_income?.[month] || 0)) - (stats?.monthly_expense?.[month] || 0),
      }));
  })();

  const statusData = stats?.status_counts
    ? Object.entries(stats.status_counts).map(([status, count]) => ({ name: ORDER_STATUSES[status]?.label || status, value: count }))
    : [];

  const monthlyCustomersData = stats?.monthly_customers
    ? Object.entries(stats.monthly_customers).sort(([a], [b]) => a.localeCompare(b)).slice(-12)
        .map(([month, value]) => ({ month: new Date(month + '-01').toLocaleDateString('ru-RU', { month: 'short', year: '2-digit' }), clients: value }))
    : [];

  const filterLabel = getFilterLabel();
  const V = (v) => loading ? '...' : v;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Отчёты</h1>
          <p className="text-sm text-muted-foreground">
            Аналитика и экспорт данных
            {filterLabel && <span className="ml-2 text-foreground font-medium">• {filterLabel}</span>}
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1.5" />Экспорт
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Экспорт данных</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 pt-2">
              <Button variant="outline" className="justify-start" onClick={exportOrdersCSV}>
                <Download className="h-4 w-4 mr-2" />Заказы (CSV)
              </Button>
              <Button variant="outline" className="justify-start" onClick={exportTasksCSV}>
                <Download className="h-4 w-4 mr-2" />Задачи (CSV)
              </Button>
              <Button variant="outline" className="justify-start" onClick={exportCustomersCSV}>
                <Download className="h-4 w-4 mr-2" />Клиенты (CSV)
              </Button>
              <Button variant="outline" className="justify-start" onClick={exportTasksICS}>
                <Download className="h-4 w-4 mr-2" />Задачи (ICS-календарь)
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end flex-wrap">
            <div className="flex gap-1.5">
              {[{ label: 'Вчера', offset: -1 }, { label: 'Сегодня', offset: 0 }, { label: 'Завтра', offset: 1 }].map(({ label, offset }) => (
                <Button key={label} variant={isQuickActive(offset) ? 'default' : 'outline'} size="sm" onClick={() => setQuickFilter(offset)}>{label}</Button>
              ))}
            </div>
            <Select value={filterYear} onValueChange={(v) => { setFilterYear(v); setFilterDateFrom(''); setFilterDateTo(''); }}>
              <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Год" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все годы</SelectItem>
                {availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterMonth} onValueChange={(v) => { setFilterMonth(v); setFilterDateFrom(''); setFilterDateTo(''); }}>
              <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Месяц" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все месяцы</SelectItem>
                {MONTHS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 items-center">
              <Input type="date" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setFilterYear('all'); setFilterMonth('all'); }} className="w-full sm:w-40" />
              <span className="text-muted-foreground text-sm">—</span>
              <Input type="date" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setFilterYear('all'); setFilterMonth('all'); }} className="w-full sm:w-40" />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="shrink-0"><X className="h-4 w-4 mr-1" />Сбросить</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Общий доход</p>
            <p className="text-2xl font-bold mt-1 text-emerald-600">{V(formatCurrency(stats?.total_income))}</p>
            {(stats?.manual_income > 0 || stats?.orders_income > 0) && (
              <p className="text-[10px] text-muted-foreground mt-1">Заказы: {formatCurrency(stats?.orders_income)}{stats?.manual_income > 0 ? ` · Ручные: ${formatCurrency(stats?.manual_income)}` : ''}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Расходы</p>
            <p className="text-2xl font-bold mt-1 text-red-600">{V(formatCurrency(stats?.manual_expense || 0))}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Прибыль</p>
            <p className={`text-2xl font-bold mt-1 ${(stats?.net_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{V(formatCurrency(stats?.net_profit || 0))}</p>
            <p className="text-[10px] text-muted-foreground mt-1">Доход − Расходы</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Средний чек</p>
            <p className="text-2xl font-bold mt-1">{V(formatCurrency(stats?.avg_check))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Orders & Payment Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Заказов</p>
            <p className="text-2xl font-bold mt-1">{V(stats?.total_orders || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Предоплаты</p>
            </div>
            <p className="text-2xl font-bold mt-1">{V(formatCurrency(stats?.total_prepayment))}</p>
            <p className="text-xs text-muted-foreground">{V(`${stats?.prepayment_pct || 0}% от активных заказов`)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Конверсия</p>
            </div>
            <p className="text-2xl font-bold mt-1 text-emerald-600">{V(`${stats?.conversion_rate || 0}%`)}</p>
            <p className="text-xs text-muted-foreground">{V(`${stats?.completed_orders || 0} завершено`)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Доход/день</p>
            </div>
            <p className="text-2xl font-bold mt-1">{V(formatCurrency(stats?.avg_revenue_per_day))}</p>
            <p className="text-xs text-muted-foreground">{V(`за ${stats?.period_days || 0} дн.`)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">К получению</p>
            </div>
            <p className="text-2xl font-bold mt-1">{V(formatCurrency(stats?.total_remaining))}</p>
            <p className="text-xs text-muted-foreground">Остаток по заказам</p>
          </CardContent>
        </Card>
      </div>

      {/* Tasks & Clients Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Tasks Summary */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Задачи
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-lg bg-muted/50">
                <p className="text-2xl font-bold">{V(stats?.total_tasks || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Всего</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                <p className="text-2xl font-bold text-emerald-600">{V(stats?.completed_tasks || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Выполнено</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                <p className="text-2xl font-bold text-amber-600">{V(stats?.pending_tasks || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">В ожидании</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-950/30">
                <p className="text-2xl font-bold text-red-600">{V(stats?.overdue_tasks || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Просрочено</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Customers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Топ клиентов
            </CardTitle>
            <CardDescription>{stats?.customers_count || 0} клиентов всего</CardDescription>
          </CardHeader>
          <CardContent>
            {!loading && stats?.top_customers?.length > 0 ? (
              <div className="space-y-2">
                {stats.top_customers.map((c, i) => (
                  <div key={c.id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}.</span>
                      <span className="text-sm font-medium truncate max-w-[160px]">{c.name}</span>
                      <span className="text-xs text-muted-foreground">{c.orders_count} заказов</span>
                    </div>
                    <span className="text-sm font-semibold">{formatCurrency(c.total)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
                {loading ? 'Загрузка...' : 'Нет данных'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Доходы и расходы</CardTitle>
            <CardDescription>{filterLabel ? `За период: ${filterLabel}` : 'Динамика по месяцам'}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[300px]" /> : monthlyRevenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    formatter={(value, name) => [formatCurrency(value), name === 'revenue' ? 'Доход' : 'Расходы']}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '6px' }}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.1} name="revenue" />
                  <Area type="monotone" dataKey="expense" stroke="#EF4444" fill="#EF4444" fillOpacity={0.1} name="expense" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">Нет данных за выбранный период</div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Распределение по статусам</CardTitle>
            <CardDescription>{filterLabel ? `За период: ${filterLabel}` : 'Заказы по статусам'}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[300px]" /> : statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={2} dataKey="value"
                    label={({ name, percent }) => window.innerWidth > 640 ? `${name} ${(percent * 100).toFixed(0)}%` : `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {statusData.map((_, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">Нет данных за выбранный период</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Прибыль по месяцам</CardTitle>
            <CardDescription>{filterLabel ? `За период: ${filterLabel}` : 'Доход − Расходы'}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[300px]" /> : monthlyRevenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), 'Прибыль']}
                    contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '6px' }}
                  />
                  <Bar dataKey="profit" radius={[4, 4, 0, 0]} name="profit">
                    {monthlyRevenueData.map((entry, index) => (
                      <Cell key={`profit-${index}`} fill={entry.profit >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">Нет данных за выбранный период</div>
            )}
          </CardContent>
        </Card>

        {/* Monthly New Customers Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Динамика клиентов</CardTitle>
            <CardDescription>{filterLabel ? `Новые клиенты за период: ${filterLabel}` : 'Новые клиенты по месяцам'}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-[300px]" /> : monthlyCustomersData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyCustomersData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '6px' }} />
                  <Bar dataKey="clients" name="Клиенты" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">Нет данных за выбранный период</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
